// Utility functions for dynamic column filtering based on data availability

/**
 * Filters columns based on whether they have data in the dataset
 * @param {Array} allColumns - All possible column definitions
 * @param {Array} data - The actual data array
 * @returns {Array} - Filtered columns that have data
 */
export const getDynamicColumns = (allColumns, data) => {
  if (!data || data.length === 0) {
    return allColumns; // Return all columns if no data
  }

  // Get all unique field names that have non-empty values in the data
  const fieldsWithData = new Set();

  data.forEach((row) => {
    Object.keys(row).forEach((key) => {
      const value = row[key];
      // Consider a field as having data if it's not null, undefined, or empty string
      // Include 0, -999, and NaN as valid values for metrics (they will be displayed as "-")
      if (value !== null && value !== undefined && value !== '') {
        fieldsWithData.add(key);
      }
    });
  });

  const essentialFields = ['campaign_name', 'country', 'os_name'];

  const totalMeasures = Array.from(fieldsWithData).filter(
    (field) =>
      field.toLowerCase().includes('total') ||
      field.toLowerCase().includes('ecpe') ||
      field.toLowerCase().includes('ecpi') ||
      field.toLowerCase().startsWith('skan_') ||
      field.toLowerCase().startsWith('mmp_')
  );

  const filteredColumns = allColumns.filter((column) => {
    if (essentialFields.includes(column.field)) {
      return true;
    }

    if (totalMeasures.includes(column.field)) {
      return true;
    }

    return fieldsWithData.has(column.field);
  });

  return filteredColumns;
};

/**
 * Gets available fields from data for debugging
 * @param {Array} data - The actual data array
 * @returns {Array} - Array of field names that have data
 */
export const getAvailableFields = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  const fieldsWithData = new Set();

  data.forEach((row) => {
    Object.keys(row).forEach((key) => {
      const value = row[key];
      if (value !== null && value !== undefined && value !== '' && value !== '-999') {
        fieldsWithData.add(key);
      }
    });
  });

  return Array.from(fieldsWithData);
};

/**
 * Checks if a specific field has data in the dataset
 * @param {Array} data - The actual data array
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if field has data
 */
export const hasFieldData = (data, fieldName) => {
  if (!data || data.length === 0) {
    return false;
  }

  return data.some((row) => {
    const value = row[fieldName];
    return value !== null && value !== undefined && value !== '';
  });
};
